import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  UserPlus,
  Shield,
  Truck,
  Shirt,
  Wind,
  CheckCircle,
  Users,
  Target,
  Heart,
  Lightbulb,
  Building,
  Clock,
  ChevronRight,
  ArrowRight,
  ChevronLeft,
  ShoppingCart,
  Star,
  Zap,
  Home,
  Droplets,
  Leaf,
  Microscope,
  Sprout,
  Recycle,
  HelpCircle,
  LogOut,
  Frown,
  Smile,
  Briefcase,
  BookOpen,
  ZoomIn,
  ZoomOut,
  FileText,
  CheckSquare,
  Factory,
  AlertTriangle,
  Info,
  Calendar,
  RefreshCw,
  Wallet,
  Calculator,
  CreditCard,
  Banknote,
  Baby,
} from "lucide-react";

// --- Custom Styles for Animation ---
const CustomStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@300;400;500;700&display=swap');
    
    .animate-fade-in {
      animation: fadeIn 0.5s ease-in-out;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }

    body {
      font-family: 'Noto Sans JP', sans-serif;
    }
    
    html {
      scroll-behavior: smooth;
    }

    .rental-table th, .rental-table td {
      border: 1px solid #e5e7eb;
      padding: 0.75rem;
      text-align: center;
      font-size: 0.875rem;
    }
    .rental-table th {
      background-color: #f0fdf4;
      color: #065f46;
      font-weight: bold;
      white-space: nowrap;
    }
    .rental-table tr:nth-child(even) {
      background-color: #f9fafb;
    }
  `}</style>
);

// --- Animation Utilities ---
const useOnScreen = (ref, rootMargin = "0px") => {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIntersecting(true);
          observer.unobserve(entry.target);
        }
      },
      { rootMargin }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [ref, rootMargin]);
  return isIntersecting;
};

const FadeIn = ({ children, delay = 0, className = "", direction = "up" }) => {
  const ref = useRef();
  const onScreen = useOnScreen(ref, "-50px");

  const getTransform = () => {
    if (!onScreen) {
      switch (direction) {
        case "up":
          return "translate-y-8";
        case "left":
          return "-translate-x-8";
        case "right":
          return "translate-x-8";
        default:
          return "";
      }
    }
    return "translate-y-0 translate-x-0";
  };

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out transform ${
        onScreen ? "opacity-100" : "opacity-0"
      } ${getTransform()}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// --- Navigation Overlay (Simple Menu Button) ---
const Navigation = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);

  const menuItems = [
    { label: "ホーム", id: "home" },
    { label: "サービス", id: "services_scroll" },
    { label: "ニュース", id: "news" },
    { label: "会社概要", id: "company_scroll" },
    { label: "採用情報", id: "recruit" },
  ];

  const handleNav = (id) => {
    setIsOpen(false);
    onNavigate(id);
  };

  return (
    <>
      {/* Company Logo (Fixed Top-Left) */}
      <button
        onClick={() => onNavigate("home")}
        className="fixed top-6 left-4 md:left-6 z-50 flex items-center gap-2 group transition-transform hover:scale-105 origin-left"
      >
        <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border border-emerald-100 group-hover:bg-white transition-all flex items-center gap-3">
          <img
            src="https://lh3.googleusercontent.com/d/1KGlDmX_NFBGgzy_C5tig8NEg7g-b9-uu"
            alt="OHRIC"
            className="h-8 md:h-10 w-auto object-contain"
          />
          <span className="text-emerald-900 font-bold text-sm md:text-base tracking-wide">
            オーリック株式会社
          </span>
        </div>
      </button>

      {/* Floating Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-6 right-4 md:right-6 z-50 bg-emerald-900 text-white p-3 rounded-full shadow-lg hover:bg-emerald-800 transition-colors"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Fullscreen Overlay Menu */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-emerald-900/95 backdrop-blur-sm flex items-center justify-center animate-fade-in">
          <nav className="flex flex-col items-center space-y-6">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className="text-white text-2xl font-bold hover:text-lime-400 transition-colors"
              >
                {item.label}
              </button>
            ))}
            <div className="pt-8 border-t border-white/20 w-48 text-center">
              <button
                onClick={() => handleNav("contact")}
                className="bg-white text-emerald-900 font-bold py-3 px-8 rounded-full hover:bg-lime-400 transition-colors"
              >
                お問い合わせ
              </button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

// --- Page Components ---

// 1. オゾンレンタル問い合わせフォーム
const OzoneRentalContactPage = ({ onBack, onRequestPrivacy }) => {
  const [form, setForm] = useState({
    type: "お問合せ",
    name: "",
    company: "",
    email: "",
    emailConfirm: "",
    phone: "",
    address: "",
    purpose: "",
    place: "",
    model: "",
    period: "",
    payment: "",
    message: "",
  });
  const [agreed, setAgreed] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreed) return;
    if (form.email !== form.emailConfirm) {
      alert("確認用メールアドレスが一致しません。");
      return;
    }
    // メール送信ロジック（文字化け対策：encodeURIComponentを使用）
    const subject = `【オゾンレンタル問い合わせ】${form.name}様より`;
    let body = `■内容：${form.type}\r\n\r\n`;
    body += `■お客様情報\r\n`;
    body += `お名前：${form.name}\r\n`;
    body += `会社名：${form.company}\r\n`;
    body += `メールアドレス：${form.email}\r\n`;
    body += `電話番号：${form.phone}\r\n`;
    body += `ご住所：${form.address}\r\n\r\n`;
    body += `■利用詳細\r\n`;
    body += `利用の目的：${form.purpose}\r\n`;
    body += `利用の場所：${form.place}\r\n`;
    body += `ご希望の機種：${form.model}\r\n`;
    body += `ご希望レンタル期間：${form.period}\r\n\r\n`;
    body += `■その他\r\n`;
    body += `お支払方法（ご注文の場合）：${form.payment}\r\n`;
    body += `お問合せ・お見積内容：${form.message}`;

    window.location.href = `mailto:ozone@ohric.jp?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="animate-fade-in bg-slate-50 min-h-screen pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-10">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
            オゾン発生器ご購入・レンタルに関するお問い合わせ
          </h1>
          <p className="text-gray-600">必要事項をご記入の上、ご送信下さい。</p>
        </FadeIn>

        <FadeIn
          delay={200}
          className="bg-white rounded-xl shadow-lg border-t-4 border-teal-600 overflow-hidden p-6 md:p-10"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid md:grid-cols-3 gap-4 items-start border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700 pt-1">
                内容{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2 space-y-2">
                {["お問合せ", "お見積り", "その他"].map((item) => (
                  <label
                    key={item}
                    className="flex items-center cursor-pointer mr-6 inline-flex"
                  >
                    <input
                      type="radio"
                      name="type"
                      value={item}
                      checked={form.type === item}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                    />
                    <span className="ml-2 text-gray-700">{item}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                ご氏名{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="例：山田 太郎"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                会社名（法人の場合）{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleInputChange}
                  placeholder="例：株式会社オーリック（個人の場合は「個人」と入力）"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-start border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700 pt-2">
                メールアドレス{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2 space-y-4">
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="例：taro@ohric.jp"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
                <div>
                  <p className="text-xs text-gray-500 mb-1">確認のため再入力</p>
                  <input
                    type="email"
                    name="emailConfirm"
                    value={form.emailConfirm}
                    onChange={handleInputChange}
                    placeholder="例：taro@ohric.jp"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                お電話番号{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleInputChange}
                  placeholder="例：093-371-3711"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                ご住所（市、町まで）
              </label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleInputChange}
                  placeholder="ご注文の場合は〒も記入下さい"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                利用の目的{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="purpose"
                  value={form.purpose}
                  onChange={handleInputChange}
                  placeholder="例：展示会の感染予防、別荘の消臭等"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">ご利用の場所</label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="place"
                  value={form.place}
                  onChange={handleInputChange}
                  placeholder="例：コンサートホール、自宅等"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">ご希望の機種</label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="model"
                  value={form.model}
                  onChange={handleInputChange}
                  placeholder="例：剛腕１０００やエールピュール等"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                ご希望レンタル期間
              </label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="period"
                  value={form.period}
                  onChange={handleInputChange}
                  placeholder="例：６月１日から５日まで等"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-start border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700 pt-1">
                お支払方法
                <br />
                <span className="text-xs font-normal">（ご注文の場合）</span>
              </label>
              <div className="md:col-span-2 space-y-2">
                {["事前振込", "クレジットカード", "請求書"].map((item) => (
                  <label
                    key={item}
                    className="flex items-center cursor-pointer mr-6 inline-flex"
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={item}
                      checked={form.payment === item}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                    />
                    <span className="ml-2 text-gray-700">{item}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-start">
              <label className="font-bold text-gray-700 pt-2">
                お問合せ・お見積内容
              </label>
              <div className="md:col-span-2">
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="ご質問やご要望がございましたらご記入ください"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                ></textarea>
              </div>
            </div>

            <div className="pt-8 text-center border-t border-gray-200 mt-6">
              <div className="flex items-center justify-center mb-6">
                <label className="flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    <span
                      className="text-teal-600 hover:underline cursor-pointer font-bold"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onRequestPrivacy) onRequestPrivacy();
                      }}
                    >
                      プライバシーポリシー
                    </span>
                    に同意する
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  type="button"
                  onClick={onBack}
                  className="bg-gray-200 text-gray-700 font-bold text-lg px-8 py-3 rounded-full hover:bg-gray-300 transition-colors"
                >
                  戻る
                </button>
                <button
                  type="submit"
                  disabled={!agreed}
                  className={`bg-teal-600 text-white font-bold text-lg px-12 py-3 rounded-full shadow-lg transform transition-all ${
                    agreed
                      ? "hover:bg-teal-700 hover:-translate-y-1 hover:shadow-xl active:scale-95"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  <Mail className="inline-block mr-2" /> 送信画面へ
                </button>
              </div>
              <p className="mt-4 text-xs text-gray-500">
                ※送信後、お使いのメールソフトが起動します。内容をご確認の上、送信してください。
              </p>
            </div>
          </form>
        </FadeIn>
      </div>
    </div>
  );
};

// 1.5 一般お問い合わせフォーム
const GeneralContactPage = ({ onBack, onRequestPrivacy }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    message: "",
  });
  const [agreed, setAgreed] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreed) return;

    // 文字化け対策: encodeURIComponentを使用
    const subject = `【一般のお問い合わせ】${form.name}様より`;
    let body = `■お名前：${form.name}\r\n`;
    body += `■会社名：${form.company}\r\n`;
    body += `■電話番号：${form.phone}\r\n`;
    body += `■メールアドレス：${form.email}\r\n`;
    body += `■お問い合わせ内容：\r\n${form.message}`;

    window.location.href = `mailto:info@ohric.jp?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="animate-fade-in bg-slate-50 min-h-screen pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-10">
          <span className="inline-block bg-emerald-100 text-emerald-800 text-xs md:text-sm font-bold px-4 py-1 rounded-full mb-4">
            お問い合わせ（代表）
          </span>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            一般のお問い合わせ
          </h1>
          <p className="text-gray-600 mt-4">
            ユニフォーム、採用、その他一般的なお問い合わせはこちらから承ります。
          </p>
        </FadeIn>

        <FadeIn
          delay={200}
          className="bg-white rounded-xl shadow-lg border-t-4 border-emerald-600 overflow-hidden p-6 md:p-10"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                お名前{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="例：山田 太郎"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">会社名</label>
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleInputChange}
                  placeholder="例：株式会社オーリック"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                電話番号{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleInputChange}
                  placeholder="例：090-1234-5678"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-center border-b border-gray-100 pb-6">
              <label className="font-bold text-gray-700">
                メールアドレス{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="例：info@ohric.jp"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 items-start">
              <label className="font-bold text-gray-700 pt-2">
                お問い合わせ内容{" "}
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded ml-2">
                  必須
                </span>
              </label>
              <div className="md:col-span-2">
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="ご質問などをご記入ください"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                ></textarea>
              </div>
            </div>

            <div className="pt-8 text-center border-t border-gray-200 mt-6">
              <div className="flex items-center justify-center mb-6">
                <label className="flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    <span
                      className="text-emerald-600 hover:underline cursor-pointer font-bold"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onRequestPrivacy) onRequestPrivacy();
                      }}
                    >
                      プライバシーポリシー
                    </span>
                    に同意する
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  type="button"
                  onClick={onBack}
                  className="bg-gray-200 text-gray-700 font-bold text-lg px-8 py-3 rounded-full hover:bg-gray-300 transition-colors"
                >
                  戻る
                </button>
                <button
                  type="submit"
                  disabled={!agreed}
                  className={`bg-emerald-600 text-white font-bold text-lg px-12 py-3 rounded-full shadow-lg transform transition-all ${
                    agreed
                      ? "hover:bg-emerald-700 hover:-translate-y-1 hover:shadow-xl active:scale-95"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  <Mail className="inline-block mr-2" /> 送信画面へ
                </button>
              </div>
              <p className="mt-4 text-xs text-gray-500">
                ※送信後、お使いのメールソフトが起動します。内容をご確認の上、送信してください。
              </p>
            </div>
          </form>
        </FadeIn>
      </div>
    </div>
  );
};

// 2. 購入前確認ページ
const PurchaseAgreementPage = ({ product, onAgree, onCancel }) => {
  const [checked, setChecked] = useState(false);
  if (!product) return null;

  return (
    <div className="animate-fade-in">
      <section className="pt-32 pb-20 bg-slate-50 min-h-screen">
        <FadeIn className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="bg-orange-500 px-6 py-4 flex items-center text-white">
              <AlertTriangle className="mr-3" size={28} />
              <h1 className="text-xl font-bold">ご購入前の重要な確認事項</h1>
            </div>

            <div className="p-8">
              <div className="flex flex-col md:flex-row gap-6 mb-8 border-b border-gray-100 pb-8">
                <div className="w-full md:w-1/3 flex justify-center items-center bg-gray-100 rounded-lg p-4">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="max-h-32 object-contain"
                    referrerPolicy="no-referrer"
                  />
                </div>
                <div className="w-full md:w-2/3">
                  <p className="text-sm text-gray-500 mb-1">購入製品</p>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {product.name}
                  </h2>
                  <p className="text-emerald-600 font-bold text-lg">
                    {product.price}
                  </p>
                </div>
              </div>

              <div className="space-y-6 text-gray-700">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-start">
                  <Info
                    className="text-blue-600 mr-3 shrink-0 mt-0.5"
                    size={20}
                  />
                  <p className="text-sm text-blue-800">
                    本製品は、有人環境でも安全に使用できる「低濃度オゾン」を発生させる設計となっておりますが、お客様に安心・安全にご利用いただくため、以下の内容をご確認ください。
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="text-emerald-500 mr-2" size={20} />
                    使用上の注意
                  </h3>
                  <ul className="list-disc pl-6 space-y-2 text-sm leading-relaxed">
                    <li>
                      <strong>換気について：</strong>{" "}
                      オゾン特有のにおいが気になる場合や、閉め切った部屋で長時間使用する場合は、適度な換気を行ってください。
                    </li>
                    <li>
                      <strong>使用距離：</strong>{" "}
                      オゾン吹き出し口に顔（鼻や口）を極端に近づけた状態で、長時間吸引し続けないでください。
                    </li>
                    <li>
                      <strong>体調への配慮：</strong>{" "}
                      喘息や呼吸器系に疾患をお持ちの方は、医師にご相談の上、ご自身の体調に合わせてご使用ください。万が一体調が悪くなった場合は直ちに使用を中止してください。
                    </li>
                    <li>
                      <strong>取扱説明書：</strong>{" "}
                      商品に同梱されている取扱説明書を必ずお読みいただき、記載された使用方法・メンテナンス方法を守ってご使用ください。
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-gray-100">
                <label className="flex items-center p-4 rounded-lg hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-all">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                  />
                  <span className="ml-3 font-bold text-gray-800">
                    上記の注意事項を確認し、内容に同意します。
                  </span>
                </label>

                <div className="flex flex-col md:flex-row gap-4 mt-6 justify-center">
                  <button
                    onClick={onCancel}
                    className="px-6 py-3 rounded-lg border border-gray-300 text-gray-600 font-bold hover:bg-gray-50 transition-colors w-full md:w-auto"
                  >
                    キャンセル（戻る）
                  </button>
                  <button
                    onClick={() => onAgree(product)}
                    disabled={!checked}
                    className={`px-8 py-3 rounded-lg font-bold text-white shadow-lg transition-all w-full md:w-auto flex items-center justify-center ${
                      checked
                        ? "bg-orange-600 hover:bg-orange-700"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    同意して購入ページへ進む
                  </button>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );
};

// 3. オゾンレンタルLP
const OzoneRentalPage = ({ onRequestContact }) => {
  const shortTermRates = [
    {
      name: "剛腕1000",
      basic: "8,000",
      ext: "1,500",
      week1: "15,500",
      week2: "26,000",
      week3: "36,500",
      month1: "52,000",
    },
    {
      name: "エールピュール",
      basic: "5,000",
      ext: "1,200",
      week1: "11,000",
      week2: "19,400",
      week3: "27,800",
      month1: "39,000",
    },
    {
      name: "エアフィーノ",
      basic: "3,000",
      ext: "800",
      week1: "7,000",
      week2: "12,600",
      week3: "18,200",
      month1: "26,000",
    },
    {
      name: "エールピュールドーム",
      basic: "2,000",
      ext: "500",
      week1: "4,500",
      week2: "8,000",
      week3: "11,500",
      month1: "16,000",
    },
    {
      name: "爽やかウインド",
      basic: "1,500",
      ext: "300",
      week1: "3,000",
      week2: "5,100",
      week3: "7,200",
      month1: "10,000",
    },
    {
      name: "爽やかイオンプラス",
      basic: "1,000",
      ext: "200",
      week1: "2,000",
      week2: "3,400",
      week3: "4,800",
      month1: "6,500",
    },
  ];

  const longTermRates = [
    {
      name: "剛腕1000",
      m1: "52,000",
      m2: "47,000",
      m3: "43,000",
      m4_6: "40,000",
      m7: "38,000",
    },
    {
      name: "エールピュール",
      m1: "39,000",
      m2: "34,000",
      m3: "30,000",
      m4_6: "27,000",
      m7: "25,000",
    },
    {
      name: "エアフィーノ",
      m1: "26,000",
      m2: "24,500",
      m3: "23,000",
      m4_6: "21,500",
      m7: "20,000",
    },
    {
      name: "エールピュールドーム",
      m1: "16,000",
      m2: "15,000",
      m3: "14,000",
      m4_6: "13,000",
      m7: "12,000",
    },
    {
      name: "爽やかウインド",
      m1: "10,000",
      m2: "8,500",
      m3: "7,500",
      m4_6: "6,500",
      m7: "6,000",
    },
    {
      name: "爽やかイオンプラス",
      m1: "6,500",
      m2: "5,700",
      m3: "5,000",
      m4_6: "4,300",
      m7: "4,000",
    },
  ];

  const shippingFees = [
    { area: "北海道・沖縄", groupA: "6,500", groupB: "5,500" },
    { area: "東北", groupA: "5,500", groupB: "4,500" },
    { area: "関東・甲信越", groupA: "5,000", groupB: "4,000" },
    { area: "東海・北陸", groupA: "4,500", groupB: "3,500" },
    { area: "関西・中四国", groupA: "4,000", groupB: "3,000" },
    { area: "九州", groupA: "4,000", groupB: "3,000" },
  ];

  const rentalProducts = [
    {
      name: "剛腕1000TR2",
      type: "高濃度・無人下",
      image:
        "https://lh3.googleusercontent.com/d/1kvfyVCTLBYBYyNS04v3nTdlGyfkYwHA8",
      desc: "スタンダードな高濃度オゾン発生器。24時間タイマー搭載。",
      usage: "トイレ、オフィス、厨房、病室、倉庫、待合室、教室",
      spec: "発生量: 250-1000mg/h, 重量: 5kg",
      note: "無人下での使用限定",
    },
    {
      name: "エールピュール (AP-500NT)",
      type: "高濃度・無人下",
      image:
        "https://lh3.googleusercontent.com/d/1f770ubqLsjaLO1gk56rMY-0QnHVKVmnX",
      desc: "60分タイマー装備。シガレットコード付きで車用にも。",
      usage: "客室、厨房、作業場、研究室、車両（バス・タクシー・救急車）",
      spec: "発生量: 500mg/h, 重量: 3kg",
      note: "無人下での使用限定",
    },
    {
      name: "エアフィーノ (VS-50S)",
      type: "中低濃度・有人下OK",
      image:
        "https://lh3.googleusercontent.com/d/1wsZMh9i3GjtESpjmaI5vAhMICiWcZIKX",
      desc: "デザイン性とメンテナンス重視。人感センサー搭載。",
      usage: "オフィス、待合室、店舗、教室",
      spec: "発生量: 10-50mg/h, 推奨広さ: 6〜60畳",
      note: "有人下で使用可能",
    },
    {
      name: "エールピュールドーム (AP-20)",
      type: "中低濃度・有人下OK",
      image:
        "https://lh3.googleusercontent.com/d/1lUOlvc5jNm6TxAtECMt7s7HeKqNmFaba",
      desc: "ドーム型のユニークなデザイン。人感センサー搭載。",
      usage: "トイレ、オフィス、小規模スペース",
      spec: "発生量: 10-20mg/h, 推奨広さ: 20〜40㎡",
      note: "有人下で使用可能",
    },
    {
      name: "爽やかウインド (AP-8)",
      type: "中低濃度・有人下OK",
      image:
        "https://lh3.googleusercontent.com/d/1vTvqncoVvjNHtr23rQMbEysRoY484V7S",
      desc: "多頭飼いの方や広いリビングに。優れた消臭効果。",
      usage: "ペット臭、タバコ臭、トイレ臭、加齢臭",
      spec: "発生量: 4-8mg/h, 推奨広さ: 最大30畳",
      note: "有人下で使用可能",
    },
    {
      name: "爽やかイオンプラス (CS-4)",
      type: "中低濃度・有人下OK",
      image:
        "https://lh3.googleusercontent.com/d/1wR5C-D6-pp5ApfPnPPfdyICe-RAxzBtK",
      desc: "イオン風生成器で拡散。静音設計でランニングコスト安。",
      usage: "ペット臭、トイレ臭、生活臭（個室・会議室）",
      spec: "発生量: 1-4mg/h, 推奨広さ: 最大20畳",
      note: "有人下で使用可能",
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-teal-100/50 rounded-bl-[100px] z-0"></div>
        <div className="max-w-6xl mx-auto px-4 relative z-10 flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-10 md:mb-0">
            <FadeIn>
              <span className="inline-block bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                法人・個人事業主様向け
              </span>
              <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
                「買う」より手軽に。
                <br className="hidden md:inline" />
                <span className="text-teal-600">オゾン機器レンタル</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                感染症対策や強力脱臭に。高性能な業務用オゾン発生器を、
                必要な期間だけ、リーズナブルに導入しませんか？
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={onRequestContact}
                  className="bg-teal-600 text-white font-bold py-4 px-8 rounded-full shadow-lg hover:bg-teal-700 transition-all transform hover:-translate-y-1"
                >
                  レンタルのご相談・お見積り
                </button>
                <a
                  href="#price-list"
                  className="py-4 px-8 rounded-full border-2 border-teal-600 text-teal-600 font-bold hover:bg-teal-50 transition-colors"
                >
                  料金表を見る
                </a>
              </div>
            </FadeIn>
          </div>
          <div className="md:w-1/2 flex justify-center">
            <FadeIn delay={200} direction="left">
              <img
                src="https://lh3.googleusercontent.com/d/1EiL_OfdYNoWdO-C6zCACfwkjRTqQvV5m"
                alt="オゾンレンタルイメージ"
                className="rounded-xl shadow-2xl"
                referrerPolicy="no-referrer"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Merits */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Wallet,
                title: "初期費用を大幅カット",
                desc: "高額な業務用機器も、レンタルなら月々のわずかな費用で導入可能。",
              },
              {
                icon: Calendar,
                title: "1週間からの短期利用",
                desc: "「イベント期間中だけ」「冬場だけ」など必要な期間だけ利用可能。",
              },
              {
                icon: RefreshCw,
                title: "メンテナンスも安心",
                desc: "万が一の故障時も代替機を即座にご用意。定期的なメンテナンスプランもございます。",
              },
            ].map((item, idx) => (
              <FadeIn
                key={idx}
                delay={idx * 100}
                className="bg-teal-50 p-6 rounded-xl text-center border border-teal-100"
              >
                <div className="bg-white w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-teal-600">
                  <item.icon size={28} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Rental Products */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <FadeIn className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              レンタル対象商品
            </h2>
            <p className="text-gray-600">
              用途に合わせて高濃度タイプ・中低濃度タイプをお選びいただけます。
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8">
            {rentalProducts.map((prod, idx) => (
              <FadeIn
                key={idx}
                delay={idx * 100}
                className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col md:flex-row hover:shadow-lg transition-shadow"
              >
                <div className="md:w-2/5 bg-gray-100 flex items-center justify-center p-4">
                  <img
                    src={prod.image}
                    alt={prod.name}
                    className="max-h-40 object-contain"
                    referrerPolicy="no-referrer"
                  />
                </div>
                <div className="p-6 md:w-3/5 flex flex-col">
                  <div className="mb-2">
                    <span
                      className={`text-[10px] font-bold px-2 py-1 rounded text-white ${
                        prod.type.includes("高濃度")
                          ? "bg-orange-500"
                          : "bg-teal-500"
                      }`}
                    >
                      {prod.type}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {prod.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">{prod.desc}</p>
                  <div className="mt-auto text-xs text-gray-500 space-y-1">
                    <p>
                      <span className="font-bold">用途:</span> {prod.usage}
                    </p>
                    <p>
                      <span className="font-bold">仕様:</span> {prod.spec}
                    </p>
                    {prod.note && (
                      <p className="text-orange-600 font-bold">※{prod.note}</p>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Price Info (Short Term) */}
      <section id="price-list" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <FadeIn>
            <div className="border-l-8 border-teal-600 pl-6 mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                レンタル料金
              </h2>
              <p className="text-gray-600">
                期間に合わせて「短期」と「長期」の2つのプランをご用意しています。
              </p>
            </div>
          </FadeIn>

          {/* Short Term */}
          <FadeIn className="mb-20">
            <h3 className="text-2xl font-bold text-teal-700 mb-6 flex items-center">
              <Calendar className="mr-2" /> 短期レンタル（1ヶ月以内・日割り）
            </h3>

            {/* Calculation Example */}
            <div className="bg-orange-50 p-6 rounded-xl border border-orange-200 mb-8">
              <h4 className="font-bold text-orange-800 mb-4 flex items-center">
                <FileText className="mr-2" size={20} />
                計算例：剛腕1000を4月1日〜6日までレンタルする場合（6日間）
              </h4>
              <div className="flex flex-col md:flex-row gap-4 text-sm text-gray-700 bg-white p-4 rounded-lg shadow-sm">
                <div className="flex-1 space-y-2">
                  <p>
                    <strong>① 基本料金（1泊2日）:</strong> 8,000円
                  </p>
                  <p>
                    <strong>② 延長料金（4日分）:</strong> 1,500円 × 4 = 6,000円
                  </p>
                  <p>
                    <strong>③ 往復運賃（大阪想定）:</strong> 4,000円
                  </p>
                </div>
                <div className="flex items-center justify-center text-gray-400">
                  <ArrowRight className="hidden md:block" />
                  <span className="md:hidden">▼</span>
                </div>
                <div className="flex-1 flex flex-col justify-center items-center bg-orange-100 rounded p-4">
                  <span className="text-xs text-orange-800 font-bold mb-1">
                    合計（税別）
                  </span>
                  <span className="text-2xl font-bold text-orange-600">
                    18,000円
                  </span>
                  <span className="text-[10px] text-gray-500">＋消費税</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-right">
                ※商品は前日に配達します。返却はご利用の翌日までに発送してください。
              </p>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full rental-table min-w-[800px]">
                <thead>
                  <tr>
                    <th>商品名</th>
                    <th>
                      基本料金
                      <br />
                      <span className="text-xs font-normal">(1泊2日)</span>
                    </th>
                    <th>
                      延長料金
                      <br />
                      <span className="text-xs font-normal">(/日)</span>
                    </th>
                    <th>1週間</th>
                    <th>2週間</th>
                    <th>3週間</th>
                    <th>
                      1ヶ月
                      <br />
                      <span className="text-xs font-normal">(最大料金)</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shortTermRates.map((row, idx) => (
                    <tr key={idx}>
                      <td className="font-bold text-left bg-white">
                        {row.name}
                      </td>
                      <td className="text-orange-600 font-bold">{row.basic}</td>
                      <td>{row.ext}</td>
                      <td>{row.week1}</td>
                      <td>{row.week2}</td>
                      <td>{row.week3}</td>
                      <td className="font-bold bg-teal-50">{row.month1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-right mt-2 text-gray-500">
              ※単位：円（税別） ※別途、往復運賃がかかります。
            </p>
          </FadeIn>

          {/* Long Term */}
          <FadeIn>
            <h3 className="text-2xl font-bold text-teal-700 mb-6 flex items-center">
              <Calendar className="mr-2" /> 長期レンタル（1ヶ月以上・月割）
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              月額料金＋往復送料のみ。基本料金は不要です。月の途中での返却でも1ヶ月分の費用となります。
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full rental-table min-w-[800px]">
                <thead>
                  <tr>
                    <th>商品名</th>
                    <th>1ヶ月</th>
                    <th>2ヶ月</th>
                    <th>3ヶ月</th>
                    <th>4〜6ヶ月</th>
                    <th>7ヶ月以上</th>
                  </tr>
                </thead>
                <tbody>
                  {longTermRates.map((row, idx) => (
                    <tr key={idx}>
                      <td className="font-bold text-left bg-white">
                        {row.name}
                      </td>
                      <td>{row.m1}</td>
                      <td>{row.m2}</td>
                      <td>{row.m3}</td>
                      <td>{row.m4_6}</td>
                      <td className="text-teal-700 font-bold">{row.m7}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>

          {/* Shipping Info */}
          <FadeIn className="bg-gray-100 p-6 rounded-xl border border-gray-200 mt-12">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center">
              <Truck className="mr-2" size={20} /> 往復運賃表（税別）
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-sm font-bold bg-gray-200 p-2 rounded mb-2">
                  【グループA】大型・中型機種
                </h4>
                <p className="text-xs text-gray-600 mb-2">
                  対象：剛腕1000、エールピュール、エアフィーノ
                </p>
                <div className="bg-white rounded border border-gray-300 p-2">
                  <ul className="text-sm space-y-1">
                    {shippingFees.map((fee, idx) => (
                      <li
                        key={idx}
                        className="flex justify-between border-b border-dashed border-gray-200 last:border-0 py-1"
                      >
                        <span>{fee.area}</span>
                        <span className="font-bold">{fee.groupA}円</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold bg-gray-200 p-2 rounded mb-2">
                  【グループB】小型機種
                </h4>
                <p className="text-xs text-gray-600 mb-2">
                  対象：爽やかウインド、爽やかイオンプラス、エールピュールドーム
                </p>
                <div className="bg-white rounded border border-gray-300 p-2">
                  <ul className="text-sm space-y-1">
                    {shippingFees.map((fee, idx) => (
                      <li
                        key={idx}
                        className="flex justify-between border-b border-dashed border-gray-200 last:border-0 py-1"
                      >
                        <span>{fee.area}</span>
                        <span className="font-bold">{fee.groupB}円</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Payment Info */}
          <FadeIn className="mt-12">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">
              お支払いについて
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-700">
              <div>
                <h4 className="font-bold text-teal-700 mb-2 flex items-center">
                  <Briefcase size={16} className="mr-1" /> 短期レンタル
                </h4>
                <p>
                  商品代引き、事前お振込、クレジットカード、請求書払い（法人・個人事業主様のみ）がご利用いただけます。
                </p>
              </div>
              <div>
                <h4 className="font-bold text-teal-700 mb-2 flex items-center">
                  <FileText size={16} className="mr-1" /> 長期レンタル
                </h4>
                <p>
                  事前お振込、クレジットカード、請求書払いに対応。
                  <br />
                  ※3ヶ月以内の場合は一括前払い、4ヶ月以上の場合は3ヶ月分前払い＋4ヶ月目以降月払いとなります。
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <FadeIn>
            <h2 className="text-3xl font-bold mb-6">
              まずは、お気軽にご相談ください
            </h2>
            <p className="text-emerald-100 mb-10">
              設置場所の広さや目的に合わせて、最適なプランと機種をご提案いたします。
            </p>
            <button
              onClick={onRequestContact}
              className="bg-white text-teal-800 font-bold py-4 px-12 rounded-full shadow-lg hover:bg-gray-100 transition-all transform hover:-translate-y-1"
            >
              お問い合わせフォームへ
            </button>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

// 4. HomePage Component
const HomePage = ({ onRequestChangePage }) => {
  const issues = [
    {
      id: "rentall",
      icon: Truck,
      color: "lime",
      title: "「イベント準備、何からすればいい？」「機材が足りない…」",
      solution:
        "イベント・レンタル事業が解決します。会場設営から備品手配まで、イベントのプロがトータルサポート。",
      reasons: [
        "年間多数のイベント現場実績",
        "設営スタッフによる安心サポート",
        "北九州・福岡エリアに密着した対応",
      ],
      action: "ダスキンレントオールの詳細へ",
      link: "rentall",
    },
    {
      id: "ozone",
      icon: Shield,
      color: "teal",
      title: "「ニオイが消えない」「感染症対策を強化したい」",
      solution:
        "環境衛生（オゾン）事業が解決します。残留性のない強力なオゾンの力で、空間を丸ごとリセット。",
      reasons: [
        "30年以上のオゾン機器取扱い実績",
        "救急車やホテルへの導入事例多数",
        "デモ機による事前の効果確認が可能",
      ],
      action: "オゾン事業詳細へ",
      link: "ozone",
    },
    {
      id: "uniform",
      icon: Shirt,
      color: "emerald",
      title: "「制服管理が面倒」「クリーニングの手間を減らしたい」",
      solution:
        "ユニフォーム事業が解決します。清潔なユニフォームのリース・クリーニングを一括管理。",
      reasons: [
        "衛生環境が整った工場でのクリーニング",
        "業種に合わせた最適な運用提案",
        "補修・在庫管理もワンストップ",
      ],
      action: "お問い合わせ",
      link: "contact",
    },
    {
      id: "air",
      icon: Wind,
      color: "green",
      title: "「エアコンの効きが悪い」「電気代が高い」",
      solution:
        "空調事業（サンレイ空調）が解決します。店舗・工場の空調設備設計から施工、メンテナンスまで。",
      reasons: [
        "幅広い施設での施工実績",
        "空調＋電気＋衛生環境のトータル設計",
        "地域密着の迅速な保守対応",
      ],
      action: "サンレイ空調公式サイトへ",
      link: "http://sanreykucho.jp/",
    },
  ];

  const historyData = [
    {
      year: "昭和2年",
      title: "大内商事合資会社 創業",
      description: "大内健二が戸畑にて創業。",
    },
    {
      year: "昭和34年",
      title: "株式会社大内商会に組織変更",
      description: "八幡に本社を移転。",
    },
    {
      year: "昭和52年",
      title: "㈱クキノウミドライを関連会社とする",
      description: "",
    },
    {
      year: "昭和58年",
      title: "㈱ダスキンの協栄工場となる",
      description: "",
    },
    {
      year: "昭和60年",
      title: "ユナイテッドレントオール小倉ストア開設",
      description: "㈱ダスキンの加盟店としてオープン。",
    },
    {
      year: "平成3年",
      title: "社名を「オーリック株式会社」に変更",
      description: "",
    },
    {
      year: "平成4年",
      title: "新工場設立",
      description: "門司区に㈱クキノウミドライの新工場を新設。",
    },
    {
      year: "平成9年",
      title: "ダスキン福岡北工場 開設",
      description: "ダスキン事業の拡大。",
    },
    // Removed ISO14001 entry for Heisei 12
    {
      year: "平成21年",
      title: "本社移転・合併",
      description: "㈱クキノウミドライと合併し、門司区奥田に本社を移転。",
    },
    {
      year: "平成24年",
      title: "空調事業を取得",
      description: "睦電機工業㈱から空調事業を営業譲渡される。",
    },
    {
      year: "平成25年",
      title: "博多イベントセンター開設",
      description: "福岡エリアへのサービス強化。",
    },
    {
      year: "平成29年",
      title: "株式会社サンレイ空調を子会社化",
      description: "",
    },
    {
      year: "平成30年",
      title: "クリーニング営業権取得",
      description: "協和ドライクリーニングの営業権を取得。",
    },
    {
      year: "令和元年",
      title: "ダスキンユニフォームサービス加盟",
      description: "",
    },
    {
      year: "令和6年",
      title: "石油事業を譲渡",
      description: "㈱ワシダへ事業譲渡（事業ポートフォリオの最適化）。",
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section
        id="home"
        className="relative pt-32 pb-24 bg-emerald-950 text-white overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900 to-teal-800 opacity-80 z-10"></div>
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80"
            alt="Office Background"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center md:text-left">
          <FadeIn delay={100}>
            <div className="inline-block bg-lime-500/90 text-emerald-900 px-3 py-1 rounded-full text-xs font-bold mb-6">
              EST. 1959 - NEXT STAGE
            </div>
          </FadeIn>
          <FadeIn delay={300}>
            <h1
              className="text-3xl md:text-5xl font-extrabold mb-4 leading-tight"
              style={{ fontFamily: "sans-serif" }}
            >
              人と場の“ありたい姿”をかたちにし、
              <br className="hidden md:block" />
              九州のお客様に愛していただき66年
            </h1>
          </FadeIn>
          <FadeIn delay={500}>
            <p className="text-xl md:text-2xl font-bold mb-8 text-emerald-200">
              オーリックは新たなステージへ
            </p>
          </FadeIn>
          <FadeIn
            delay={700}
            className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
          >
            <a
              href="#services"
              className="px-8 py-4 bg-white text-emerald-900 rounded-lg font-bold shadow-lg hover:bg-emerald-50 text-center transition-all hover:scale-105"
            >
              お悩みから探す
            </a>
            <button
              onClick={() => onRequestChangePage("company_scroll")}
              className="px-8 py-4 border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg font-bold hover:bg-white/20 text-center transition-all hover:scale-105"
            >
              会社概要
            </button>
          </FadeIn>
        </div>
      </section>

      {/* MVV & Values */}
      <section className="py-20 bg-emerald-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
          <FadeIn>
            <h2 className="text-lime-400 font-bold mb-2">Our Philosophy</h2>
            <h3 className="text-3xl font-extrabold mb-12">
              オーリックの存在意義
            </h3>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <FadeIn
              delay={200}
              direction="right"
              className="bg-emerald-900/50 p-8 rounded-xl relative overflow-hidden border border-emerald-800 hover:border-lime-500/50 transition-colors"
            >
              <span className="text-6xl font-black text-white/5 absolute -top-4 -left-4 select-none pointer-events-none">
                MISSION
              </span>
              <h4 className="text-2xl font-bold mb-4 relative z-10 text-lime-400">
                MISSION
              </h4>
              <p className="relative z-10">
                私たちは、環境に優しい商品とサービスを通じて、
                <br className="hidden md:inline" />
                安心で快適な暮らしを社会に届けます。
              </p>
            </FadeIn>
            <FadeIn
              delay={400}
              direction="left"
              className="bg-emerald-900/50 p-8 rounded-xl relative overflow-hidden border border-emerald-800 hover:border-lime-500/50 transition-colors"
            >
              <span className="text-6xl font-black text-white/5 absolute -top-4 -left-4 select-none pointer-events-none">
                VISION
              </span>
              <h4 className="text-2xl font-bold mb-4 relative z-10 text-lime-400">
                VISION
              </h4>
              <p className="relative z-10">
                人と場の “ありたい姿” をかたちにし、
                <br className="hidden md:inline" />
                持続可能で豊かな未来を創造します。
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={600}>
            <h4 className="text-xl font-bold mb-8 text-emerald-200">
              行動指針 (Values)
            </h4>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                title: "信頼",
                desc: "誠実さを基盤に、社会とお客様の期待に応える",
              },
              {
                title: "協働",
                desc: "多様な力を結集し、新しい価値を共に生み出す",
              },
              { title: "挑戦", desc: "変化を好機と捉え、常に進化を続ける" },
              { title: "責任", desc: "成果と学びを、次世代へつなげる" },
            ].map((v, i) => (
              <FadeIn
                key={i}
                delay={700 + i * 100}
                className="bg-emerald-900 p-6 rounded-lg border border-emerald-800 hover:bg-emerald-800 transition-colors"
              >
                <h5 className="text-lg font-bold mb-2 text-white">{v.title}</h5>
                <p className="text-sm text-emerald-300">{v.desc}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* 事業案内 */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
              お悩みから探す
            </h2>
            <p className="text-gray-600">
              お客様の課題に合わせて、最適なソリューションをご提案します。
            </p>
          </FadeIn>
          <div className="grid grid-cols-1 gap-8">
            {issues.map((item, idx) => {
              const Icon = item.icon;
              return (
                <FadeIn
                  key={item.id}
                  delay={idx * 150}
                  className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="grid md:grid-cols-2 h-full">
                    <div
                      className={`bg-${item.color}-50 p-8 border-b md:border-b-0 md:border-r border-${item.color}-100 flex flex-col`}
                    >
                      <div className="flex items-center mb-4">
                        <div
                          className={`p-3 bg-${item.color}-100 text-${item.color}-600 rounded-xl mr-4`}
                        >
                          <Icon size={32} />
                        </div>
                        <span
                          className={`text-${item.color}-600 font-bold text-sm uppercase tracking-wider`}
                        >
                          お悩み・課題
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 whitespace-pre-line leading-relaxed">
                        {item.title}
                      </h3>
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="mb-6">
                        <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                          <Smile className={`text-${item.color}-600 mr-2`} />{" "}
                          オーリックならこう解決します
                        </h4>
                        <p className="text-gray-600 leading-relaxed text-sm whitespace-pre-line">
                          {item.solution}
                        </p>
                      </div>
                      <div className="mb-8">
                        <h4 className="text-sm font-bold text-gray-500 mb-3 uppercase tracking-wider border-b pb-1">
                          選ばれる理由
                        </h4>
                        <ul className="space-y-2">
                          {item.reasons.map((reason, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-sm text-gray-700"
                            >
                              <CheckCircle
                                size={16}
                                className={`text-${item.color}-500 mr-2 mt-0.5 flex-shrink-0`}
                              />
                              {reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <button
                        onClick={() => onRequestChangePage(item.link)}
                        className={`w-full bg-${item.color}-600 text-white font-bold py-4 rounded-lg hover:bg-${item.color}-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center mt-auto`}
                      >
                        {item.action} <ArrowRight className="ml-2" size={18} />
                      </button>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* ダスキン生産事業（協力工場）紹介セクション */}
      <section
        id="production"
        className="py-20 bg-slate-50 border-t border-b border-gray-200"
      >
        <FadeIn className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="md:w-1/2">
              <span className="text-gray-500 font-bold tracking-widest text-xs md:text-sm uppercase mb-2 block flex items-center">
                <Factory size={16} className="mr-2" /> PRODUCTION & LOGISTICS
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4 leading-tight">
                ダスキン生産事業
                <br />
                <span className="text-lg md:text-xl font-normal text-gray-500">
                  （ダスキン福岡北工場）
                </span>
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm md:text-base">
                ダスキンの協力工場として、マット・モップを中心に、洗浄、乾燥、検査を行う加工機能と、ダスキン支店、加盟店との製品の入出荷を行う物流機能をもつ総合工場として稼働しています。
                <br />
                <br />
                レンタルを基本とした循環型ビジネスを展開し、廃棄物削減や省資源化といった環境保全に努めています。
              </p>

              <div className="flex flex-wrap gap-4 text-sm font-bold text-gray-500">
                <span className="flex items-center bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm">
                  <RefreshCw size={16} className="mr-2 text-green-600" />
                  循環型ビジネス
                </span>
                <span className="flex items-center bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm">
                  <Truck size={16} className="mr-2 text-blue-600" />
                  総合物流機能
                </span>
                <span className="flex items-center bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm">
                  <Leaf size={16} className="mr-2 text-emerald-600" />
                  環境保全
                </span>
              </div>
            </div>

            <div className="md:w-1/2 w-full">
              <div className="bg-white rounded-2xl h-64 md:h-80 relative overflow-hidden border border-gray-200 shadow-sm group">
                <img
                  src="https://lh3.googleusercontent.com/d/1HXqeI2jQtKQs1_mhQtPHp_FfxryyLJtn"
                  alt="ダスキン福岡北工場"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  style={{ objectPosition: "25% center" }}
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80"></div>
                <div className="absolute bottom-4 left-6 text-white">
                  <p className="text-xs font-bold tracking-widest uppercase opacity-90">
                    DUSKIN COOPERATION FACTORY
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* 新体制・会社概要・沿革 */}
      <section id="company" className="py-20 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4">
          <FadeIn className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              新体制・会社概要
            </h2>
          </FadeIn>
          <FadeIn
            delay={200}
            className="bg-gray-50 p-8 rounded-xl mb-12 border-l-4 border-emerald-600 shadow-sm"
          >
            <h3 className="font-bold text-emerald-700 mb-8 text-xl border-b border-gray-200 pb-2">
              TOP MESSAGE
            </h3>

            {/* 代表取締役メッセージ */}
            <div className="mb-10 pb-8 border-b border-gray-200 border-dashed">
              <div className="text-gray-700 leading-loose text-base mb-6 font-medium">
                <p className="mb-4">
                  平素は格別のご高配を賜り、厚く御礼申し上げます。
                </p>
                <p>
                  オーリック株式会社は、創業以来、地域の皆様に支えられながら、多様なサービスを通じて社会への貢献に努めてまいりました。これからも変わらぬご支援とご愛顧を賜りますようお願い申し上げます。
                </p>
              </div>
              <div className="text-right mt-4">
                <p className="text-sm text-gray-500 mb-1">代表取締役社長</p>
                <p className="text-xl font-bold text-gray-900">門野 美郎</p>
              </div>
            </div>

            {/* 専務取締役メッセージ (レイアウト変更) */}
            <div className="mb-4">
              <div className="text-gray-700 leading-loose text-base mb-6 font-medium">
                <p>
                  創業から66年、私たちは九州北部で5つの事業を展開し、地域の皆様と共に歩んでまいりました。この度、さらなる事業成長とサービスの向上を目指し、新体制へと移行いたしました。現場を支えるメンバーはそのままに、これまで培ってきた「信頼」と「技術」を次世代へと繋いでまいります。
                </p>
              </div>
              <div className="text-right mt-4">
                <p className="text-sm text-gray-500 mb-1">専務取締役</p>
                <p className="text-xl font-bold text-gray-900">三浦 一将</p>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={300} className="border-t pt-12 mb-12">
            <h4 className="text-xl font-bold mb-6">会社概要</h4>
            <dl>
              <div className="bg-gray-50 px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-gray-100">
                <dt className="text-sm font-bold text-gray-500">名称</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-medium">
                  オーリック株式会社
                </dd>
              </div>
              <div className="bg-white px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-gray-100">
                <dt className="text-sm font-bold text-gray-500">代表者</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  代表取締役社長　門野 美郎
                </dd>
              </div>
              <div className="bg-gray-50 px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-gray-100">
                <dt className="text-sm font-bold text-gray-500">役員</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  専務取締役　三浦 一将
                  <br />
                  取締役本部長　村上 真哉
                  <br />
                  取締役工場長　森光 将之
                  <br />
                  社外取締役　佐々木 淳
                </dd>
              </div>
              <div className="bg-white px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-gray-100">
                <dt className="text-sm font-bold text-gray-500">本社所在地</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  〒800-0011 福岡県北九州市門司区奥田2-6-1
                </dd>
              </div>
              <div className="bg-gray-50 px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-gray-100">
                <dt className="text-sm font-bold text-gray-500">資本金</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  3,800万円
                </dd>
              </div>
              <div className="bg-white px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-gray-100">
                <dt className="text-sm font-bold text-gray-500">設立</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  昭和34年
                </dd>
              </div>
              <div className="bg-gray-50 px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <dt className="text-sm font-bold text-gray-500">事業内容</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  イベント・ベビー用品レンタル（ダスキンレントオール）
                  <br />
                  オゾン機器販売・レンタル
                  <br />
                  ユニフォームサービス
                  <br />
                  ダスキン生産事業
                  <br />
                  その他環境関連事業
                </dd>
              </div>
            </dl>
          </FadeIn>

          <FadeIn delay={400} className="border-t pt-12">
            <h4 className="text-xl font-bold mb-6 flex items-center justify-center">
              <Clock className="mr-2" size={24} /> 沿革
            </h4>
            <div className="relative border-l-2 border-gray-200 ml-6 space-y-8">
              {historyData.map((item, index) => (
                <div key={index} className="relative pl-8">
                  <div className="absolute -left-[9px] top-1 bg-white border-4 border-emerald-500 w-5 h-5 rounded-full"></div>
                  <div className="flex flex-col sm:flex-row sm:items-baseline">
                    <span className="text-base md:text-lg font-bold text-emerald-800 w-24 shrink-0 font-mono">
                      {item.year}
                    </span>
                    <div>
                      <h3 className="text-base md:text-lg font-bold text-gray-900">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

// 5. 製品カードコンポーネント (画像単一表示・修正版)
const OzoneProductCard = ({ product, onProductSelect, onPurchaseClick }) => {
  return (
    <div className="bg-white rounded-xl shadow p-6 flex flex-col relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
      {product.featured && (
        <div className="absolute top-0 right-0 bg-yellow-400 text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
          人気No.1
        </div>
      )}

      {/* メイン画像表示 */}
      <div className="h-48 bg-gray-100 mb-4 flex items-center justify-center overflow-hidden rounded-lg">
        <img
          src={product.image}
          alt={product.name}
          className="max-h-full max-w-full object-contain transition-opacity duration-300"
          referrerPolicy="no-referrer"
        />
      </div>

      <div className="mb-2">
        {product.tags.map((tag) => (
          <span
            key={tag}
            className="inline-block bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded mr-1"
          >
            {tag}
          </span>
        ))}
      </div>
      <h3 className="text-lg font-bold mb-1">{product.name}</h3>
      <p className="text-sm text-gray-600 mb-4 flex-grow">{product.desc}</p>

      <div className="mt-auto">
        <p className="text-lg font-bold text-emerald-600 mb-2">
          {product.price}
        </p>
        <button
          onClick={() => onProductSelect(product.name)}
          className="w-full bg-teal-600 text-white py-2 rounded hover:bg-teal-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center"
        >
          <Mail size={16} className="inline mr-2" />
          見積依頼
        </button>
      </div>
    </div>
  );
};

// 6. オゾン事業ページ（製品情報更新）
const OzonePage = ({ onProductSelect, onPurchaseClick, onRequestRental }) => {
  const [productCategory, setProductCategory] = useState("all");

  const products = [
    {
      id: "new_myair",
      name: "快適マイエアー (OZ-3)",
      categories: ["home"],
      price: "¥18,000（税抜）",
      desc: "防水仕様でトイレや水回りにも最適。シンプルで使いやすいモデル。",
      tags: ["家庭用", "防水"],
      image:
        "https://lh3.googleusercontent.com/d/1_GzR2LUNCQq0iCkZXFYPA9CXQhXoLbcV",
      featured: false,
    },
    {
      id: "sawayaka_plus",
      name: "爽やかイオンプラス (CS-4)",
      categories: ["home"],
      price: "¥37,000（税抜）",
      desc: "イオンの力でリラックス空間を演出。コンパクトで静かな運転音。",
      tags: ["家庭用", "イオン発生"],
      image:
        "https://lh3.googleusercontent.com/d/1wR5C-D6-pp5ApfPnPPfdyICe-RAxzBtK",
      featured: false,
    },
    {
      id: "sawayaka_wind",
      name: "爽やかウインド (AP-8)",
      categories: ["home"],
      price: "¥57,000（税抜）",
      desc: "静音設計。寝室や書斎など静かな場所でも気にならない。",
      tags: ["家庭用", "静音"],
      image:
        "https://lh3.googleusercontent.com/d/1vTvqncoVvjNHtr23rQMbEysRoY484V7S",
      featured: false,
    },
    {
      id: "salas_sa4",
      name: "オゾンエアー サラス (SA-4)",
      categories: ["home"],
      price: "¥21,800（税抜）",
      desc: "充電式で持ち運び可能。靴箱、クローゼット、浴室のカビ抑制に。",
      tags: ["家庭用", "充電式"],
      image:
        "https://lh3.googleusercontent.com/d/1wx1uzVQlaScf7LnbXHBmONRFi4FLJ2wF",
      featured: true,
    },
    {
      id: "ap_20",
      name: "エールピュールドーム (AP-20)",
      categories: ["home", "biz_space"],
      price: "¥98,000（税抜）", // Changed price
      desc: "ドーム型のユニークなデザイン。人感センサー搭載。",
      tags: ["家庭用", "業務用", "人感センサー"],
      image:
        "https://lh3.googleusercontent.com/d/1lUOlvc5jNm6TxAtECMt7s7HeKqNmFaba",
      featured: false,
    },
    {
      id: "airfino",
      name: "エアフィーノ (VS-50S II)",
      categories: ["biz_space"],
      price: "¥198,000（税抜）",
      desc: "保育園、病院、オフィスに。人感センサー搭載で安全に24時間除菌。",
      tags: ["業務用", "人感センサー"],
      image:
        "https://lh3.googleusercontent.com/d/1wsZMh9i3GjtESpjmaI5vAhMICiWcZIKX",
      featured: true,
    },
    {
      id: "gouwan650",
      name: "剛腕 650F2",
      categories: ["powerful"],
      price: "¥238,000（税抜）",
      desc: "ホテル客室やレンタカーの短時間脱臭に。軽量ポータブル。そのほか、剛腕シリーズは700／1000／1400もございます。",
      tags: ["強力脱臭", "ポータブル"],
      image:
        "https://lh3.googleusercontent.com/d/1nNCzDCbcfgGyaeHqtFZ3k50iZDn9kw5s",
      featured: false,
    },
    {
      id: "gouwan2800",
      name: "剛腕 2800FR2",
      categories: ["powerful"],
      price: "¥628,000（税抜）",
      desc: "シリーズ最強のオゾン発生器。広い空間や強いニオイを一気にリセット。",
      tags: ["最強力", "特殊清掃"],
      image:
        "https://lh3.googleusercontent.com/d/1yCvr0I_QsRv3lUR7j2E4c7pqBGpTkGm4",
      featured: false,
    },
    {
      id: "sania",
      name: "サニアクリーン（SC－0410M）",
      categories: ["powerful"],
      price: "¥598,000（税抜）",
      desc: "オゾン水生成器。厨房の手洗いや器具洗浄に。",
      tags: ["オゾン水", "厨房"],
      image:
        "https://lh3.googleusercontent.com/d/1rHV6Hg2Pra1T2wRLKF1c3rSvCPD-v_kG",
      featured: false,
    },
    {
      id: "sensui1000",
      name: "仙水1000（AS-1000DS）",
      categories: ["powerful"],
      price: "¥780,000（税抜）",
      desc: "グリーストラップ用オゾン浄化装置。油脂分解・悪臭対策に。",
      tags: ["グリーストラップ", "油脂分解"],
      image:
        "https://lh3.googleusercontent.com/d/1AA6crF6_7AKAVbIOvaS8uZe5wcW5htlx",
      featured: false,
    },
  ];

  const filteredProducts =
    productCategory === "all"
      ? products
      : products.filter((p) => p.categories.includes(productCategory));

  return (
    <div className="animate-fade-in">
      <section className="relative pt-28 pb-16 bg-gradient-to-b from-teal-50 to-white text-center overflow-hidden">
        <div className="relative z-10">
          <FadeIn>
            <h1 className="text-4xl font-extrabold text-emerald-900 mb-6">
              オゾンの力で、空間をリセット。
            </h1>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#products"
                className="inline-block bg-teal-600 text-white px-8 py-3 rounded-full shadow-lg hover:bg-teal-700 transition-all hover:-translate-y-1"
              >
                製品一覧を見る
              </a>
              <button
                onClick={onRequestRental}
                className="inline-block bg-white border-2 border-teal-600 text-teal-600 px-8 py-3 rounded-full shadow-lg hover:bg-teal-50 transition-all hover:-translate-y-1 font-bold"
              >
                レンタルについて
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why Ozone Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <FadeIn className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center">
              <Microscope className="mr-2 text-teal-600" />
              なぜオゾンなのか？
            </h2>
            <p className="text-gray-600">強力な酸化力と、残留しない安全性。</p>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <FadeIn delay={100} className="p-6 bg-teal-50 rounded-xl">
              <div className="text-teal-600 font-bold mb-2">①発生</div>
              <p className="text-sm">
                空気中の酸素に電気エネルギーを与えて生成します。原料は「空気」だけ。
              </p>
            </FadeIn>
            <FadeIn delay={200} className="p-6 bg-teal-50 rounded-xl">
              <div className="text-emerald-600 font-bold mb-2">②分解</div>
              <p className="text-sm">
                菌やニオイ物質に触れると強力に酸化分解し、その構造を破壊します。
              </p>
            </FadeIn>
            <FadeIn delay={300} className="p-6 bg-teal-50 rounded-xl">
              <div className="text-lime-600 font-bold mb-2">③酸素へ</div>
              <p className="text-sm">
                反応後は無害な酸素に戻ります。薬剤のように残留しないため安全です。
              </p>
            </FadeIn>
          </div>
          <FadeIn
            delay={400}
            className="mt-12 bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
          >
            <h3 className="font-bold text-lg text-gray-900 mb-2 border-l-4 border-teal-600 pl-3">
              特許技術：低温プラズマ発生体
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              オーリックが取り扱うオーニット製品は、従来の「湿気に弱い」という課題を克服した特許技術を採用。金属電極をセラミックとガラスで密閉することで、高湿度環境でも安定してオゾンを生成し、水洗いによるメンテナンスも可能です。
            </p>
          </FadeIn>
        </div>
      </section>

      <section id="products" className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <FadeIn className="text-center mb-8">
            <h2 className="text-2xl font-bold">製品一覧</h2>
          </FadeIn>

          <FadeIn
            delay={100}
            className="flex flex-wrap justify-center gap-2 mb-8"
          >
            <button
              onClick={() => setProductCategory("all")}
              className={`px-4 py-2 rounded-full text-xs md:text-sm font-bold transition-colors ${
                productCategory === "all"
                  ? "bg-teal-600 text-white"
                  : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              全て
            </button>
            <button
              onClick={() => setProductCategory("home")}
              className={`px-4 py-2 rounded-full text-xs md:text-sm font-bold transition-colors ${
                productCategory === "home"
                  ? "bg-teal-600 text-white"
                  : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              家庭用
            </button>
            <button
              onClick={() => setProductCategory("biz_space")}
              className={`px-4 py-2 rounded-full text-xs md:text-sm font-bold transition-colors ${
                productCategory === "biz_space"
                  ? "bg-teal-600 text-white"
                  : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              業務用
            </button>
            <button
              onClick={() => setProductCategory("powerful")}
              className={`px-4 py-2 rounded-full text-xs md:text-sm font-bold transition-colors ${
                productCategory === "powerful"
                  ? "bg-teal-600 text-white"
                  : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              強力脱臭
            </button>
          </FadeIn>

          <FadeIn delay={150} className="text-right mb-4 px-2">
            <p className="text-xs text-gray-600 font-medium">
              ※別途工事が発生するものは、その費用を頂戴いたします。
              <br />
              ※掲載しているもの以外にも商品がございます。お気軽にお問合せください。
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8">
            {filteredProducts.map((p, idx) => (
              <FadeIn key={p.id} delay={idx * 100}>
                <OzoneProductCard
                  product={p}
                  onProductSelect={onProductSelect}
                  onPurchaseClick={onPurchaseClick}
                />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// 7. レントオール詳細問い合わせフォーム復元
const RentAllPage = ({ onOpenCatalog, onRequestChangePage }) => {
  const [rentalForm, setRentalForm] = useState({
    type: "御見積り依頼",
    name: "",
    kana: "",
    company: "",
    phone: "",
    fax: "",
    email: "",
    zip: "",
    address: "",
    eventDateStart: "",
    eventDateEnd: "",
    eventName: "",
    deliveryType: "配達／回収",
    items: "",
    remarks: "",
  });
  const [agreed, setAgreed] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRentalForm({ ...rentalForm, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreed) return;
    alert("メーラーを起動します（デモ）");
  };

  const shops = [
    {
      name: "北九州イベントセンター",
      address: "〒807-0805　北九州市八幡西区光貞台1-3-22",
      tel: "093-521-1151",
      desc: "大規模～小規模のイベント・会場設営など、福岡北部～大分北部、山口エリアをカバー。",
      icon: Building,
      color: "emerald",
    },
    {
      name: "博多イベントセンター",
      address: "〒812-0892　福岡市博多区東那珂3-2-6",
      tel: "092-432-8686",
      desc: "大規模～小規模のイベント・会場設営など、福岡中南部～九州南東部をカバー。",
      icon: Building,
      color: "emerald",
    },
    {
      name: "久留米ステーション",
      address: "〒839-0863　久留米市国分町1469-6",
      tel: "0942-21-3020",
      desc: "ベビーベッド、チャイルドシートなど、ベビー用品のレンタル店。中～小規模イベントにも対応。",
      icon: Smile,
      color: "lime",
    },
  ];

  return (
    <div className="animate-fade-in">
      <section className="pt-32 pb-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-12">
            <span className="inline-block bg-lime-100 text-emerald-800 text-xs md:text-sm font-bold px-4 py-1 rounded-full mb-4">
              ダスキンレントオール
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              イベント・ベビー用品レンタル
            </h1>
            <p className="text-gray-600 mb-8">
              イベントの企画・設営から、ベビーベッドなどのベビー用品レンタルまで。
              <br />
              電子カタログで商品をご覧になりたい方は、下のボタンから閲覧してください。
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() =>
                  window.open(
                    "https://event.kasite.net/sys_file/arc/digitalcatalog/vol23/index.html?page=%E8%A1%A8%E7%B4%99",
                    "_blank"
                  )
                }
                className="bg-emerald-600 text-white font-bold text-lg px-8 py-4 rounded-full hover:bg-emerald-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center"
              >
                <BookOpen className="mr-2" />
                イベント備品カタログを見る
                <ExternalLink size={16} className="ml-2" />
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://www.kasite.net/catalog/pdf/catalog2025.pdf",
                    "_blank"
                  )
                }
                className="bg-lime-600 text-white font-bold text-lg px-8 py-4 rounded-full hover:bg-lime-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 flex items-center justify-center"
              >
                <Baby className="mr-2" />
                ベビー用品カタログを見る
                <ExternalLink size={16} className="ml-2" />
              </button>
            </div>
          </FadeIn>

          {/* 店舗情報 */}
          <FadeIn delay={100} className="mt-16 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
              事業所・ステーションのご案内
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {shops.map((shop, idx) => (
                <div
                  key={idx}
                  className="bg-white p-6 rounded-xl shadow-md border-t-4 border-emerald-500 hover:shadow-lg transition-shadow"
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                      shop.color === "lime"
                        ? "bg-lime-100 text-lime-600"
                        : "bg-emerald-100 text-emerald-600"
                    }`}
                  >
                    <shop.icon size={24} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{shop.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{shop.desc}</p>
                  <div className="text-sm text-gray-500 space-y-1">
                    <p className="flex items-start">
                      <MapPin size={14} className="mr-1 mt-1 shrink-0" />{" "}
                      {shop.address}
                    </p>
                    <p className="flex items-center">
                      <Phone size={14} className="mr-1" />{" "}
                      <a
                        href={`tel:${shop.tel.replace(/-/g, "")}`}
                        className="hover:text-emerald-600 underline"
                      >
                        {shop.tel}
                      </a>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn
            delay={200}
            className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mt-12"
          >
            <div className="bg-gradient-to-r from-lime-600 to-emerald-600 px-6 py-4 border-b border-emerald-700">
              <h2 className="text-white font-bold text-lg flex items-center">
                <Truck className="mr-2" />
                レンタルお問い合わせ・お見積りフォーム
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 md:p-8">
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                  ご依頼内容
                </h3>
                <div className="flex flex-wrap gap-4">
                  {["御見積り依頼", "ご予約依頼", "その他のお問合せ"].map(
                    (type) => (
                      <label
                        key={type}
                        className="flex items-center cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="type"
                          value={type}
                          checked={rentalForm.type === type}
                          onChange={handleInputChange}
                          className="w-5 h-5 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                        />
                        <span className="ml-2 text-gray-700">{type}</span>
                      </label>
                    )
                  )}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                  お客様情報
                </h3>
                <div className="grid grid-cols-1 gap-6">
                  {/* お名前 */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 flex items-center">
                      お名前{" "}
                      <span className="ml-2 bg-lime-500 text-white text-[10px] px-2 py-0.5 rounded">
                        必須
                      </span>
                    </label>
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        name="name"
                        value={rentalForm.name}
                        onChange={handleInputChange}
                        placeholder="例：山田 太郎"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  {/* カナ */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 flex items-center">
                      お名前（フリガナ）{" "}
                      <span className="ml-2 bg-lime-500 text-white text-[10px] px-2 py-0.5 rounded">
                        必須
                      </span>
                    </label>
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        name="kana"
                        value={rentalForm.kana}
                        onChange={handleInputChange}
                        placeholder="例：ヤマダ タロウ"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  {/* 会社名 */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700">
                      会社名・団体名
                    </label>
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        name="company"
                        value={rentalForm.company}
                        onChange={handleInputChange}
                        placeholder="例：株式会社オーリック"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  {/* 電話番号 */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 flex items-center">
                      電話番号{" "}
                      <span className="ml-2 bg-lime-500 text-white text-[10px] px-2 py-0.5 rounded">
                        必須
                      </span>
                    </label>
                    <div className="md:col-span-2">
                      <input
                        type="tel"
                        name="phone"
                        value={rentalForm.phone}
                        onChange={handleInputChange}
                        placeholder="例：093-000-0000"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  {/* Email */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 flex items-center">
                      メールアドレス{" "}
                      <span className="ml-2 bg-lime-500 text-white text-[10px] px-2 py-0.5 rounded">
                        必須
                      </span>
                    </label>
                    <div className="md:col-span-2">
                      <input
                        type="email"
                        name="email"
                        value={rentalForm.email}
                        onChange={handleInputChange}
                        placeholder="例：info@ohric.jp"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  {/* 住所 */}
                  <div className="grid md:grid-cols-3 gap-2 items-start border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 mt-2">
                      ご住所
                    </label>
                    <div className="md:col-span-2 space-y-2">
                      <input
                        type="text"
                        name="zip"
                        value={rentalForm.zip}
                        onChange={handleInputChange}
                        placeholder="郵便番号 (例：800-0000)"
                        className="w-1/2 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                      <input
                        type="text"
                        name="address"
                        value={rentalForm.address}
                        onChange={handleInputChange}
                        placeholder="住所 (例：福岡県北九州市...)"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                  イベント・レンタル詳細
                </h3>
                <div className="grid grid-cols-1 gap-6">
                  {/* 期間 */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 flex items-center">
                      ご利用期間{" "}
                      <span className="ml-2 bg-lime-500 text-white text-[10px] px-2 py-0.5 rounded">
                        必須
                      </span>
                    </label>
                    <div className="md:col-span-2 flex items-center space-x-2">
                      <input
                        type="date"
                        name="eventDateStart"
                        value={rentalForm.eventDateStart}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                      <span>～</span>
                      <input
                        type="date"
                        name="eventDateEnd"
                        value={rentalForm.eventDateEnd}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  {/* イベント名 */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700">
                      イベント名・目的
                    </label>
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        name="eventName"
                        value={rentalForm.eventName}
                        onChange={handleInputChange}
                        placeholder="例：夏祭りにて使用、地鎮祭のため"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  {/* お渡し方法 */}
                  <div className="grid md:grid-cols-3 gap-2 items-center border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 flex items-center">
                      商品お渡し方法{" "}
                      <span className="ml-2 bg-lime-500 text-white text-[10px] px-2 py-0.5 rounded">
                        必須
                      </span>
                    </label>
                    <div className="md:col-span-2 flex flex-col space-y-2">
                      <div className="flex flex-wrap gap-4">
                        {["来店", "配達／回収", "設営／撤去"].map((dt) => (
                          <label
                            key={dt}
                            className="flex items-center cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="deliveryType"
                              value={dt}
                              checked={rentalForm.deliveryType === dt}
                              onChange={handleInputChange}
                              className="w-5 h-5 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                            />
                            <span className="ml-2 text-gray-700">{dt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* 商品 */}
                  <div className="grid md:grid-cols-3 gap-2 items-start border-b border-gray-100 pb-4">
                    <label className="font-bold text-gray-700 mt-2">
                      ご希望商品・数量
                    </label>
                    <div className="md:col-span-2">
                      <textarea
                        name="items"
                        value={rentalForm.items}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="例：&#13;&#10;・パイプ椅子 50脚&#13;&#10;・2間3間テント 1張&#13;&#10;・ベビーベッド 1台"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      ></textarea>
                    </div>
                  </div>
                  {/* その他 */}
                  <div className="grid md:grid-cols-3 gap-2 items-start">
                    <label className="font-bold text-gray-700 mt-2">
                      その他・ご質問
                    </label>
                    <div className="md:col-span-2">
                      <textarea
                        name="remarks"
                        value={rentalForm.remarks}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="ご不明な点やご要望がございましたらご記入ください"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-emerald-500"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center pt-6">
                {/* 同意欄 */}
                <div className="flex items-center justify-center mb-4">
                  <label className="flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      <span
                        className="text-emerald-600 hover:underline cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          onRequestChangePage("privacy");
                        }}
                      >
                        プライバシーポリシー
                      </span>
                      に同意する
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={!agreed}
                  className={`bg-lime-600 text-white font-bold text-lg px-12 py-4 rounded-full shadow-lg transform transition-all ${
                    agreed
                      ? "hover:bg-lime-700 hover:-translate-y-1 hover:shadow-xl active:scale-95"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  <Mail className="inline-block mr-2" /> 上記内容で送信する
                </button>
                <p className="mt-4 text-sm text-gray-500">
                  ※送信後、お使いのメールソフトが起動します。
                  <br />
                  内容をご確認の上、送信してください。
                </p>
              </div>
            </form>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

// 8. ニュース一覧ページ
const NewsPage = () => {
  const newsItems = [
    {
      date: "2025.11.26",
      category: "リリース",
      title:
        "北九州市とYMFGキャピタルとの「中小企業の事業承継推進に関する連携協定」に基づく第 1 号案件の実現について",
      type: "release",
      links: [
        {
          label: "北九州市",
          url: "https://www.city.kitakyushu.lg.jp/contents/10700377.html",
        },
        {
          label: "YMFGキャピタル",
          url: "https://ymfg-capital.co.jp/2025/11/26/post-2705/",
        },
      ],
    },
  ];

  const getCategoryColor = (type) => {
    switch (type) {
      case "info":
        return "bg-gray-100 text-gray-600";
      case "event":
        return "bg-lime-100 text-lime-700";
      case "release":
        return "bg-emerald-100 text-emerald-700";
      case "recruit":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="animate-fade-in">
      <section className="relative pt-32 pb-20 bg-emerald-950 overflow-hidden text-center text-white">
        <div className="absolute inset-0 z-0 bg-emerald-900"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <FadeIn>
            <h1
              className="text-3xl md:text-5xl font-extrabold mb-6 tracking-tight"
              style={{ fontFamily: "sans-serif" }}
            >
              NEWS
            </h1>
            <p className="text-lg text-emerald-100">お知らせ・更新情報</p>
          </FadeIn>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-200">
            {newsItems.map((item, index) => (
              <FadeIn
                key={index}
                delay={index * 100}
                className="py-6 border-b border-gray-200 flex flex-col md:flex-row md:items-start gap-4 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-4 min-w-[180px] shrink-0 mt-1">
                  <span className="text-gray-500 text-sm font-mono">
                    {item.date}
                  </span>
                  <span
                    className={`text-xs px-3 py-1 rounded-full font-bold ${getCategoryColor(
                      item.type
                    )}`}
                  >
                    {item.category}
                  </span>
                </div>
                <div className="flex-grow">
                  <h3 className="text-gray-900 font-medium mb-3 leading-relaxed">
                    {item.title}
                  </h3>
                  {item.links && (
                    <div className="flex flex-wrap gap-3">
                      {item.links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-emerald-600 hover:text-emerald-800 hover:underline bg-emerald-50 px-3 py-1.5 rounded transition-colors hover:bg-emerald-100"
                        >
                          {link.label}{" "}
                          <ExternalLink size={14} className="ml-1.5" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// 9. プライバシーポリシーページ
const PrivacyPolicyPage = ({ onBack }) => {
  return (
    <div className="animate-fade-in">
      <section className="relative pt-32 pb-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          {/* 戻るボタンエリア */}
          <div className="mb-8">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-emerald-600 transition-colors font-bold"
            >
              <ChevronLeft size={20} className="mr-1" />
              戻る
            </button>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            プライバシーポリシー
          </h1>
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-gray-700 text-sm leading-relaxed space-y-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                1. 個人情報の定義
              </h2>
              <p>
                本プライバシーポリシーにおいて、個人情報とは、個人情報保護法第2条第1項により定義された個人情報、すなわち、生存する個人に関する情報であって、当該情報に含まれる氏名、生年月日、その他の記述等により特定の個人を識別することができるもの（他の情報と容易に照合することができ、それにより特定の個人を識別することができることとなるものを含みます。）を意味するものとします。
              </p>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                2. 個人情報の利用目的
              </h2>
              <p>当社は、お客様の個人情報を、以下の目的で利用いたします。</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>当社サービスの提供およびご案内</li>
                <li>お問い合わせへの対応</li>
                <li>採用選考およびご連絡</li>
                <li>サービスの改善・向上</li>
              </ul>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                3. 個人情報の第三者提供
              </h2>
              <p>
                当社は、個人情報保護法その他の法令に基づき開示が認められる場合を除くほか、あらかじめお客様の同意を得ないで、個人情報を第三者に提供しません。
              </p>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                4. お問い合わせ窓口
              </h2>
              <p>
                ご意見、ご質問、苦情のお申出その他利用者情報の取扱いに関するお問い合わせは、以下の窓口までお願いいたします。
              </p>
              <p className="mt-2 font-bold">
                オーリック株式会社
                <br />
                〒800-0011 福岡県北九州市門司区奥田2-6-1
              </p>
            </div>
          </div>

          {/* 下部にも戻るボタン配置 */}
          <div className="mt-12 text-center">
            <button
              onClick={onBack}
              className="bg-gray-200 text-gray-700 font-bold px-8 py-3 rounded-full hover:bg-gray-300 transition-colors"
            >
              前のページへ戻る
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

// 10. 採用情報ページ
const RecruitPage = ({ onRequestPrivacy }) => {
  const [recruitForm, setRecruitForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [agreed, setAgreed] = useState(false);
  const contactFormRef = useRef(null);

  const handleEntry = (jobTitle) => {
    setRecruitForm((prev) => ({
      ...prev,
      message: `${jobTitle}についての面談希望`,
    }));
    contactFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRecruitForm({ ...recruitForm, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!agreed) return;

    // 文字化け対策: encodeURIComponentを使用
    const subject = `【採用・面談希望】${recruitForm.name}様より`;
    let body = `■お名前：${recruitForm.name}\r\n`;
    body += `■電話番号：${recruitForm.phone}\r\n`;
    body += `■メールアドレス：${recruitForm.email}\r\n`;
    body += `■お問い合わせ内容：\r\n${recruitForm.message}`;

    window.location.href = `mailto:recruit@ohric.jp?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="animate-fade-in">
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-emerald-950 overflow-hidden text-center text-white">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950 to-green-900 opacity-90 z-10"></div>
          <img
            src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80"
            alt="Recruit Background"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <FadeIn>
            <h1
              className="text-3xl md:text-5xl font-extrabold mb-6"
              style={{ fontFamily: "sans-serif" }}
            >
              地域を支え、未来をつくる。
            </h1>
            <p className="text-lg text-emerald-100">
              オーリックで、新しいキャリアをスタートさせませんか？
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Message */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-16">
            <span className="text-emerald-600 font-bold tracking-widest uppercase mb-2 text-sm">
              RECRUITING MESSAGE
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
              求めるのは、「誠実さ」と「挑戦心」
            </h2>
          </FadeIn>
          <FadeIn
            delay={200}
            className="bg-emerald-50/50 rounded-2xl p-8 md:p-12 border border-emerald-100 shadow-sm flex flex-col md:flex-row gap-8 items-center"
          >
            <div className="md:w-1/2">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                安定した基盤と、ベンチャーのような活気
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                オーリックは創業から60年以上の歴史を持ちますが、決して古いだけの会社ではありません。
                事業承継を経て新体制となり、これまでの信頼を守りながらも、新しい技術やサービスへの挑戦を続けています。
              </p>
              <p className="text-gray-600 leading-relaxed">
                「指示を待つ」のではなく「どうすればお客様に喜んでもらえるか」を考えられる方、
                そして地域のインフラを支える責任感を持って仕事に取り組める方を歓迎します。
              </p>
            </div>
            <div className="md:w-1/2 grid grid-cols-2 gap-4 w-full">
              {[
                {
                  Icon: UserPlus,
                  color: "text-emerald-500",
                  title: "未経験歓迎",
                  desc: "丁寧にサポートします",
                },
                {
                  Icon: Heart,
                  color: "text-pink-500",
                  title: "地域貢献",
                  desc: "地元密着の仕事です",
                },
                {
                  Icon: Target,
                  color: "text-green-500",
                  title: "資格支援",
                  desc: "スキルアップを応援",
                },
                {
                  Icon: Smile,
                  color: "text-orange-500",
                  title: "アットホーム",
                  desc: "風通しの良い社風",
                },
              ].map((item, idx) => (
                <FadeIn
                  key={idx}
                  delay={300 + idx * 100}
                  className="bg-white p-6 rounded-xl shadow-sm text-center border-b-4 border-lime-500 hover:shadow-md transition-shadow"
                >
                  <item.Icon
                    className={`mx-auto ${item.color} mb-3`}
                    size={32}
                  />
                  <h4 className="font-bold text-gray-800">{item.title}</h4>
                  <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                </FadeIn>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 募集要項 */}
      <section className="py-20 bg-gray-50" id="jobs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
              募集職種
            </h2>
            <p className="text-gray-600 mb-4">
              現在、以下の職種で新しい仲間を募集しています。
            </p>
            <p className="text-lg text-emerald-600 font-bold">
              まずは、オーリックについて知る、簡単な面談から行ってみませんか？
            </p>
          </FadeIn>
          <FadeIn
            delay={100}
            className="max-w-3xl mx-auto text-center mb-12 bg-yellow-50 p-4 rounded-lg border border-yellow-200"
          >
            <p className="text-sm text-yellow-800 font-bold">
              ※条件等は前職の経験等を考慮いたします。
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 各職種カード */}
            {[
              {
                title: "総合営業職",
                icon: Briefcase,
                color: "blue",
                desc: "法人のお客様へ、イベント用品のレンタル提案や、オゾン機器による衛生管理の提案を行います。",
                checks: [
                  "既存顧客のフォローが中心",
                  "イベント会場での打ち合わせ",
                  "見積書作成・提案資料作成",
                ],
              },
              {
                title: "配送・イベント設営",
                icon: Truck,
                color: "lime",
                desc: "レンタル商品の配送・納品や、イベント会場のテント設営・撤去作業を行います。",
                checks: [
                  "北九州・福岡エリアへの配送",
                  "イベント会場の設営サポート",
                  "商品のメンテナンス管理",
                ],
              },
              {
                title: "工場内業務",
                icon: Factory,
                color: "teal",
                desc: "回収されたユニフォームやマット・モップの洗浄、仕上げ、検品作業を行います。",
                checks: [
                  "洗濯・乾燥・仕上げ作業",
                  "仕分け・配送業務",
                  "未経験・UIターン歓迎",
                ],
              },
              {
                title: "一般事務・営業事務",
                icon: Building,
                color: "green",
                desc: "電話応対、受注処理、伝票作成など、バックオフィスから会社を支える仕事です。",
                checks: [
                  "総務・人事・経理",
                  "PC入力・伝票発行",
                  "顧客対応・秘書業務",
                ],
              },
            ].map((job, idx) => {
              const borderClass =
                job.color === "lime"
                  ? "border-lime-500"
                  : job.color === "teal"
                  ? "border-teal-500"
                  : job.color === "blue"
                  ? "border-emerald-500"
                  : "border-green-500";
              const btnClass =
                job.color === "lime"
                  ? "bg-lime-500 hover:bg-lime-600"
                  : job.color === "teal"
                  ? "bg-teal-500 hover:bg-teal-600"
                  : job.color === "blue"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-green-500 hover:bg-green-600";
              const iconColor =
                job.color === "lime"
                  ? "text-lime-500"
                  : job.color === "teal"
                  ? "text-teal-500"
                  : job.color === "blue"
                  ? "text-emerald-500"
                  : "text-green-500";
              const checkColor =
                job.color === "lime"
                  ? "text-lime-500"
                  : job.color === "teal"
                  ? "text-teal-400"
                  : job.color === "blue"
                  ? "text-emerald-400"
                  : "text-green-400";

              return (
                <FadeIn
                  key={idx}
                  delay={200 + idx * 100}
                  className={`bg-white rounded-xl overflow-hidden shadow-lg border-t-4 ${borderClass} flex flex-col hover:shadow-2xl transition-shadow transform hover:-translate-y-1`}
                >
                  <div className="p-6 flex-grow">
                    <div className="flex items-center mb-4">
                      <job.icon className={`${iconColor} mr-2`} />
                      <h3 className="text-lg font-bold text-gray-900">
                        {job.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">{job.desc}</p>
                    <ul className="text-sm text-gray-600 space-y-2 mb-6">
                      {job.checks.map((check, cIdx) => (
                        <li key={cIdx} className="flex items-start">
                          <CheckCircle
                            size={16}
                            className={`${checkColor} mr-2 mt-0.5`}
                          />
                          {check}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-6 bg-gray-50 border-t border-gray-100">
                    <button
                      onClick={() => handleEntry(job.title)}
                      className={`w-full ${btnClass} text-white font-bold py-3 rounded-lg transition-colors text-sm shadow`}
                    >
                      この職種について面談を希望する
                    </button>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* 採用エントリーフォーム */}
      <section ref={contactFormRef} className="py-20 bg-emerald-900 text-white">
        <div className="max-w-3xl mx-auto px-4">
          <FadeIn>
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                採用に関するお問い合わせ・面談希望
              </h2>
              <p className="text-emerald-100 text-sm">
                「まずは話を聞いてみたい」という方も大歓迎です。
                <br />
                以下のフォームよりお気軽にご連絡ください。
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-white text-gray-800 rounded-xl p-6 md:p-10 shadow-xl"
            >
              <div className="space-y-6">
                <div>
                  <label className="block font-bold mb-2 text-sm">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={recruitForm.name}
                    onChange={handleInputChange}
                    placeholder="例：山田 太郎"
                    required
                    className="w-full px-4 py-3 rounded border border-gray-300 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-bold mb-2 text-sm">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={recruitForm.phone}
                    onChange={handleInputChange}
                    placeholder="例：090-1234-5678"
                    required
                    className="w-full px-4 py-3 rounded border border-gray-300 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-bold mb-2 text-sm">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={recruitForm.email}
                    onChange={handleInputChange}
                    placeholder="例：taro@example.com"
                    required
                    className="w-full px-4 py-3 rounded border border-gray-300 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-bold mb-2 text-sm">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={recruitForm.message}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="ご質問や、面談希望日時などがございましたらご記入ください。"
                    required
                    className="w-full px-4 py-3 rounded border border-gray-300 focus:outline-none focus:border-emerald-500"
                  ></textarea>
                </div>
              </div>

              <div className="mt-8 text-center">
                <label className="flex items-center justify-center cursor-pointer select-none mb-6">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="ml-2 text-sm">
                    <span
                      className="text-emerald-600 font-bold underline cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onRequestPrivacy) onRequestPrivacy();
                      }}
                    >
                      プライバシーポリシー
                    </span>
                    に同意する
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={!agreed}
                  className={`w-full md:w-auto px-12 py-4 rounded-full font-bold text-lg shadow-lg transition-all transform ${
                    agreed
                      ? "bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  <Mail className="inline-block mr-2" size={20} />
                  上記内容で送信する
                </button>
                <p className="mt-4 text-xs text-gray-400">
                  ※送信後、メーラーが起動します。内容を確認して送信してください。
                </p>
              </div>
            </form>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

// 11. メインアプリケーション
const App = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [history, setHistory] = useState(["home"]);
  const [selectedProductForPurchase, setSelectedProductForPurchase] =
    useState(null);

  useEffect(() => {
    // Ensure UTF-8 meta tag exists to prevent mojibake in preview
    if (!document.querySelector("meta[charset]")) {
      const meta = document.createElement("meta");
      meta.setAttribute("charset", "UTF-8");
      document.head.prepend(meta);
    }
    if (currentPage !== "company_scroll") window.scrollTo(0, 0);
  }, [currentPage]);

  const navigate = (page) => {
    setHistory((prev) => [...prev, page]);
    setCurrentPage(page);
  };

  const goBack = () => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop();
      const prevPage = newHistory[newHistory.length - 1];
      setHistory(newHistory);
      setCurrentPage(prevPage);
    } else {
      navigate("home");
    }
  };

  const handleRequestChangePage = (page) => {
    // 外部リンク対応
    if (page.startsWith("http")) {
      window.open(page, "_blank");
      return;
    }

    if (page === "company_scroll") {
      navigate("home");
      setTimeout(
        () =>
          document
            .getElementById("company")
            ?.scrollIntoView({ behavior: "smooth" }),
        100
      );
    } else if (page === "services_scroll") {
      navigate("home");
      setTimeout(
        () =>
          document
            .getElementById("services")
            ?.scrollIntoView({ behavior: "smooth" }),
        100
      );
    } else if (page === "web_catalog") {
      window.open(
        "https://event.kasite.net/sys_file/arc/digitalcatalog/vol23/index.html?page=%E8%A1%A8%E7%B4%99",
        "_blank"
      );
    } else {
      navigate(page);
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return <HomePage onRequestChangePage={handleRequestChangePage} />;
      case "ozone":
        return (
          <OzonePage
            onProductSelect={() => navigate("contact")}
            onPurchaseClick={(p) => {
              setSelectedProductForPurchase(p);
              navigate("purchase");
            }}
            onRequestRental={() => navigate("ozone_rental")}
          />
        );
      case "ozone_rental":
        return (
          <OzoneRentalPage onRequestContact={() => navigate("ozone_contact")} />
        );
      case "ozone_contact":
        return (
          <OzoneRentalContactPage
            onBack={goBack}
            onRequestPrivacy={() => navigate("privacy")}
          />
        );
      case "general_contact":
        return (
          <GeneralContactPage
            onBack={goBack}
            onRequestPrivacy={() => navigate("privacy")}
          />
        );
      case "purchase":
        return (
          <PurchaseAgreementPage
            product={selectedProductForPurchase}
            onAgree={() => alert("購入処理へ")}
            onCancel={goBack}
          />
        );
      case "rentall":
        return <RentAllPage onRequestChangePage={navigate} />;
      case "recruit":
        return <RecruitPage onRequestPrivacy={() => navigate("privacy")} />;
      case "news":
        return <NewsPage />;
      case "privacy":
        return <PrivacyPolicyPage onBack={goBack} />;
      case "contact":
        return (
          // お問い合わせハブページ
          <div className="animate-fade-in pt-32 pb-20 min-h-screen bg-slate-50">
            <div className="max-w-4xl mx-auto px-4">
              <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                お問い合わせ
              </h1>
              <p className="text-center text-gray-600 mb-12">
                ご用件に合わせて、下記よりお問い合わせ先をお選びください。
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <button
                  onClick={() => navigate("ozone_contact")}
                  className="bg-white p-8 rounded-xl shadow border border-gray-100 hover:shadow-md transition-all text-left group"
                >
                  <div className="flex items-center mb-4 text-teal-600">
                    <Shield size={32} className="mr-3" />
                    <h3 className="font-bold text-lg text-teal-800">
                      オゾン事業・レンタル
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    オゾン発生器の購入、短期・長期レンタルのお見積り、製品に関するご質問などはこちらから。
                  </p>
                  <span className="text-teal-600 text-xs font-bold mt-4 inline-block group-hover:underline">
                    専用フォームへ進む →
                  </span>
                </button>

                <button
                  onClick={() =>
                    window.open("http://sanreykucho.jp/", "_blank")
                  }
                  className="bg-white p-8 rounded-xl shadow border border-gray-100 hover:shadow-md transition-all text-left group"
                >
                  <div className="flex items-center mb-4 text-cyan-600">
                    <Wind size={32} className="mr-3" />
                    <h3 className="font-bold text-lg text-cyan-800">
                      空調事業（サンレイ空調）
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    店舗・工場の空調設備設計、施工、メンテナンスに関するお問い合わせはこちら。
                  </p>
                  <span className="text-cyan-600 text-xs font-bold mt-4 inline-block group-hover:underline flex items-center">
                    公式サイトへ移動 <ExternalLink size={12} className="ml-1" />
                  </span>
                </button>

                <button
                  onClick={() => navigate("rentall")}
                  className="bg-white p-8 rounded-xl shadow border border-gray-100 hover:shadow-md transition-all text-left group"
                >
                  <div className="flex items-center mb-4 text-lime-600">
                    <Truck size={32} className="mr-3" />
                    <h3 className="font-bold text-lg text-lime-800">
                      イベント・レンタル
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    イベント会場設営、備品レンタル、ベビー用品レンタルに関するお問い合わせ。
                  </p>
                  <span className="text-lime-600 text-xs font-bold mt-4 inline-block group-hover:underline">
                    専用フォームへ進む →
                  </span>
                </button>

                <button
                  onClick={() => navigate("general_contact")}
                  className="bg-white p-8 rounded-xl shadow border border-gray-100 hover:shadow-md transition-all text-left group"
                >
                  <div className="flex items-center mb-4 text-emerald-600">
                    <Mail size={32} className="mr-3" />
                    <h3 className="font-bold text-lg text-emerald-800">
                      その他（代表窓口）
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm">
                    ユニフォーム、採用、その他一般的なお問い合わせはこちらから。
                  </p>
                  <span className="text-emerald-600 text-xs font-bold mt-4 inline-block group-hover:underline">
                    専用フォームへ進む →
                  </span>
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return <HomePage onRequestChangePage={handleRequestChangePage} />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <CustomStyles />
      <Navigation onNavigate={handleRequestChangePage} />
      <main>{renderPage()}</main>
      <footer className="bg-emerald-950 text-slate-400 py-12 text-center text-sm">
        <div className="flex justify-center mb-6">
          <div className="bg-white/95 backdrop-blur-sm px-6 py-3 rounded-lg inline-flex items-center gap-3">
            <img
              src="https://lh3.googleusercontent.com/d/1KGlDmX_NFBGgzy_C5tig8NEg7g-b9-uu"
              alt="OHRIC"
              className="h-8 w-auto"
            />
            <span className="text-emerald-900 font-bold text-lg tracking-wide">
              オーリック株式会社
            </span>
          </div>
        </div>
        <div className="mb-8 space-y-2 text-slate-300">
          <p>住所：〒800-0011　北九州市門司区奥田2-6-1</p>
          <p>
            電話：
            <a
              href="tel:0933713711"
              className="hover:text-white transition-colors"
            >
              093-371-3711
            </a>
          </p>
          <p>
            Email:{" "}
            <a
              href="mailto:info@ohric.jp"
              className="hover:text-white transition-colors"
            >
              info@ohric.jp
            </a>
          </p>
        </div>
        <p>&copy; {new Date().getFullYear()} OHRIC CO., LTD.</p>
      </footer>
    </div>
  );
};

export default App;
